import {
  type FC,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";

import { SubscriptionGuard } from "~/feature/subscription/components/SubscriptionGuard";
import { useSubscriptionGuard } from "~/feature/subscription/components/SubscriptionGuard/useSubscriptionGuard";
import { FeatureKey } from "~/types/subscription";

import { SpreadFilterButton } from "./components/SpreadFilterButton";
import { SpreadFilterPopover } from "./components/SpreadFilterPopover";
import { labelBySpreadFilterEnum } from "./constants";
import { SpreadFilterEnum } from "./types";
import { useSpreadFilter } from "./useSpreadFilter";

export const SpreadFilter: FC = () => {
  const {
    selectedValue,
    customSpreadCents,
    selectSpread,
    resetSpread,
    changeCustomSpreadCents,
  } = useSpreadFilter();
  const { isLocked } = useSubscriptionGuard(FeatureKey.SPREAD_FILTER);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isDefaultSelected = selectedValue === SpreadFilterEnum.All;
  const buttonLabel = useMemo(() => {
    if (
      selectedValue === SpreadFilterEnum.Custom &&
      customSpreadCents &&
      customSpreadCents > 0
    ) {
      return `${customSpreadCents}\u00A2`;
    }

    return labelBySpreadFilterEnum[selectedValue];
  }, [customSpreadCents, selectedValue]);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    if (
      selectedValue === SpreadFilterEnum.Custom &&
      (!customSpreadCents || customSpreadCents <= 0)
    ) {
      resetSpread();
    }

    setAnchorEl(null);
  }, [customSpreadCents, resetSpread, selectedValue]);

  return (
    <SubscriptionGuard featureKey={FeatureKey.SPREAD_FILTER}>
      <SpreadFilterButton
        label={buttonLabel}
        isDefaultSelected={isDefaultSelected}
        locked={isLocked}
        onOpen={handleOpen}
        onClear={resetSpread}
      />

      <SpreadFilterPopover
        anchorEl={anchorEl}
        selectedValue={selectedValue}
        customSpreadCents={customSpreadCents}
        onClose={handleClose}
        onSelect={selectSpread}
        onCustomChange={changeCustomSpreadCents}
      />
    </SubscriptionGuard>
  );
};
